import dayjs from 'dayjs';
import { z } from 'zod';

/**
 * HTML/MUI `type="number"` inputs still submit strings; empty optional fields must stay
 * `undefined` (not `0` from `Number('')`).
 */
export function preprocessOptionalNumberInput(val: unknown): unknown {
  if (val === '' || val === null || val === undefined) return undefined;
  if (typeof val === 'number') return val;
  const n = Number(val);
  return Number.isNaN(n) ? undefined : n;
}

const DEFAULT_NUMBER_INVALID = 'Invalid value';

function resolveMsg(
  specific: string | undefined,
  generic: string,
  fallback: string
): string {
  return specific?.trim() || generic || fallback;
}

export type ZOptionalNumberFromInputParams = {
  int?: boolean;
  max?: number;
  min?: number;
  /**
   * Default copy used when a constraint-specific message is not set.
   * Prefer this over {@link invalidMessage}.
   */
  message?: string;
  /** @deprecated Use {@link message} */
  invalidMessage?: string;
  /** Overrides {@link message} for invalid type only */
  invalidTypeMessage?: string;
  /** Overrides {@link message} for integer constraint only */
  intMessage?: string;
  /** Overrides {@link message} for min constraint only */
  minMessage?: string;
  /** Overrides {@link message} for max constraint only */
  maxMessage?: string;
};

function hasNumericInputConstraints(
  params?: ZOptionalNumberFromInputParams
): boolean {
  if (!params) return false;
  return (
    params.min !== undefined ||
    params.max !== undefined ||
    params.int === true ||
    !!params.message?.trim() ||
    !!params.invalidMessage?.trim() ||
    !!params.invalidTypeMessage?.trim() ||
    !!params.intMessage?.trim() ||
    !!params.minMessage?.trim() ||
    !!params.maxMessage?.trim()
  );
}

/** Same shape as {@link ZOptionalNumberFromInputParams}; used for required fields. */
export type ZNumberFromInputParams = ZOptionalNumberFromInputParams;

/**
 * Required numeric field coerced from string/number input (`z.coerce.number()`), with optional
 * `min` / `max` / `int` and the same message options as {@link zOptionalNumberFromInput}.
 */
export function zNumberFromInput(params?: ZNumberFromInputParams) {
  if (!hasNumericInputConstraints(params)) {
    return z.coerce.number();
  }

  const generic =
    params!.message?.trim() ||
    params!.invalidMessage?.trim() ||
    DEFAULT_NUMBER_INVALID;

  const msgInvalidType = resolveMsg(
    params!.invalidTypeMessage,
    generic,
    DEFAULT_NUMBER_INVALID
  );
  const msgInt = resolveMsg(params!.intMessage, generic, DEFAULT_NUMBER_INVALID);
  const msgMin = resolveMsg(params!.minMessage, generic, DEFAULT_NUMBER_INVALID);
  const msgMax = resolveMsg(params!.maxMessage, generic, DEFAULT_NUMBER_INVALID);

  let inner = z.coerce.number({ invalid_type_error: msgInvalidType });
  if (params!.int) {
    inner = inner.int({ message: msgInt });
  }
  if (params!.min !== undefined) {
    inner = inner.min(params!.min, { message: msgMin });
  }
  if (params!.max !== undefined) {
    inner = inner.max(params!.max, { message: msgMax });
  }

  return inner;
}

/**
 * Optional numeric field coerced from string/number input (e.g. controlled text fields).
 * Pass `min`, `max`, `int`, and/or messages only when you need validation beyond
 * “optional number”.
 */
export function zOptionalNumberFromInput(
  params?: ZOptionalNumberFromInputParams
) {
  if (!hasNumericInputConstraints(params)) {
    return z.preprocess(
      preprocessOptionalNumberInput,
      z.number().optional()
    );
  }

  const generic =
    params!.message?.trim() ||
    params!.invalidMessage?.trim() ||
    DEFAULT_NUMBER_INVALID;

  const msgInvalidType = resolveMsg(
    params!.invalidTypeMessage,
    generic,
    DEFAULT_NUMBER_INVALID
  );
  const msgInt = resolveMsg(params!.intMessage, generic, DEFAULT_NUMBER_INVALID);
  const msgMin = resolveMsg(params!.minMessage, generic, DEFAULT_NUMBER_INVALID);
  const msgMax = resolveMsg(params!.maxMessage, generic, DEFAULT_NUMBER_INVALID);

  let inner = z.number({ invalid_type_error: msgInvalidType });
  if (params!.int) {
    inner = inner.int({ message: msgInt });
  }
  if (params!.min !== undefined) {
    inner = inner.min(params!.min, { message: msgMin });
  }
  if (params!.max !== undefined) {
    inner = inner.max(params!.max, { message: msgMax });
  }

  return z.preprocess(preprocessOptionalNumberInput, inner.optional());
}

export const YEAR_VALIDATION_DEFAULT_MIN = 1990;
export const YEAR_VALIDATION_DEFAULT_MAX = 2100;

export type ZOptionalYearFromInputParams = {
  max?: number;
  min?: number;
  /** Default when no constraint-specific message is set */
  message?: string;
  /** @deprecated Use {@link message} */
  invalidMessage?: string;
  invalidTypeMessage?: string;
  intMessage?: string;
  minMessage?: string;
  maxMessage?: string;
};

/**
 * Optional calendar-style year (integer, bounded). Defaults: {@link YEAR_VALIDATION_DEFAULT_MIN}
 * – {@link YEAR_VALIDATION_DEFAULT_MAX}.
 */
export function zOptionalYearFromInput(
  params?: ZOptionalYearFromInputParams
) {
  const min = params?.min ?? YEAR_VALIDATION_DEFAULT_MIN;
  const max = params?.max ?? YEAR_VALIDATION_DEFAULT_MAX;
  const defaultYearMsg = `Enter a valid year (whole number between ${min} and ${max})`;
  const generic =
    params?.message?.trim() ||
    params?.invalidMessage?.trim() ||
    defaultYearMsg;

  return zOptionalNumberFromInput({
    int: true,
    max,
    message: generic,
    min,
    maxMessage: params?.maxMessage,
    minMessage: params?.minMessage,
    intMessage: params?.intMessage,
    invalidTypeMessage: params?.invalidTypeMessage,
  });
}

export type ZYearFromInputParams = ZOptionalYearFromInputParams;

/**
 * Required calendar-style year (integer, bounded). Defaults: {@link YEAR_VALIDATION_DEFAULT_MIN}
 * – {@link YEAR_VALIDATION_DEFAULT_MAX}.
 */
export function zYearFromInput(params?: ZYearFromInputParams) {
  const min = params?.min ?? YEAR_VALIDATION_DEFAULT_MIN;
  const max = params?.max ?? YEAR_VALIDATION_DEFAULT_MAX;
  const defaultYearMsg = `Enter a valid year (whole number between ${min} and ${max})`;
  const generic =
    params?.message?.trim() ||
    params?.invalidMessage?.trim() ||
    defaultYearMsg;

  return zNumberFromInput({
    int: true,
    max,
    message: generic,
    min,
    maxMessage: params?.maxMessage,
    minMessage: params?.minMessage,
    intMessage: params?.intMessage,
    invalidTypeMessage: params?.invalidTypeMessage,
  });
}

/** Matches HTML `input[type=date]` / MUI date fields that store `YYYY-MM-DD`. */
const ISO_DATE_STRING_RE = /^\d{4}-\d{2}-\d{2}$/;

const DEFAULT_DATE_INVALID = 'Enter a valid date';
const DEFAULT_DATE_REQUIRED = 'This field is required';

/**
 * Returns true when `value` is `YYYY-MM-DD` and denotes a real calendar date
 * (e.g. rejects 2024-02-30).
 */
export function isValidIsoDateString(value: string): boolean {
  if (!ISO_DATE_STRING_RE.test(value)) return false;
  const [yStr, mStr, dStr] = value.split('-');
  const y = Number(yStr);
  const m = Number(mStr);
  const d = Number(dStr);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) {
    return false;
  }
  const date = new Date(y, m - 1, d);
  return (
    date.getFullYear() === y &&
    date.getMonth() === m - 1 &&
    date.getDate() === d
  );
}

/**
 * When both bounds are non-empty and valid `YYYY-MM-DD` strings, returns whether
 * `end` is on or after `start` (lexical compare). Otherwise returns true so field-level
 * validators can surface invalid single dates first.
 */
export function isIsoDateRangeOrdered(
  start: string | undefined,
  end: string | undefined
): boolean {
  const s = typeof start === 'string' ? start.trim() : '';
  const e = typeof end === 'string' ? end.trim() : '';
  if (!s || !e) return true;
  if (!isValidIsoDateString(s) || !isValidIsoDateString(e)) return true;
  return e >= s;
}

/** True when `end` is on or after `start` for Dayjs / `Date` values; true if either side is nullish. */
export function isPickerDateRangeOrdered(start: unknown, end: unknown): boolean {
  if (start == null || end == null) return true;
  const s = dayjs.isDayjs(start) ? start : dayjs(start as Date);
  const e = dayjs.isDayjs(end) ? end : dayjs(end as Date);
  if (!s.isValid() || !e.isValid()) return true;
  return !e.isBefore(s, 'day');
}

export function isValidPickerDateValue(val: unknown): boolean {
  if (val === null || val === undefined) return false;
  if (dayjs.isDayjs(val)) return val.isValid();
  if (val instanceof Date) return !Number.isNaN(val.getTime());
  return false;
}

export type ZRequiredPickerDateParams = {
  requiredMessage?: string;
  invalidMessage?: string;
};

/**
 * Required value from MUI / Dayjs date pickers (`Dayjs` or `Date`).
 */
export function zRequiredPickerDate(params?: ZRequiredPickerDateParams) {
  const req = params?.requiredMessage?.trim() ?? DEFAULT_DATE_REQUIRED;
  const inv = params?.invalidMessage?.trim() ?? DEFAULT_DATE_INVALID;
  return z
    .any()
    .refine((val) => val !== null && val !== undefined, { message: req })
    .refine((val) => isValidPickerDateValue(val), { message: inv });
}

export type ZOptionalNullablePickerDateParams = {
  message?: string;
};

/**
 * Optional nullable picker date: `null` / `undefined` allowed; otherwise must be a valid `Dayjs` or `Date`.
 */
export function zOptionalNullablePickerDate(
  params?: ZOptionalNullablePickerDateParams
) {
  const msg = params?.message?.trim() ?? DEFAULT_DATE_INVALID;
  return z.any().refine(
    (val) =>
      val === null ||
      val === undefined ||
      isValidPickerDateValue(val),
    { message: msg }
  );
}

/**
 * Optional date fields: empty string / whitespace-only from inputs becomes `undefined`.
 */
export function preprocessOptionalDateStringInput(val: unknown): unknown {
  if (val === '' || val === null || val === undefined) return undefined;
  if (typeof val === 'string') {
    const t = val.trim();
    return t === '' ? undefined : t;
  }
  return val;
}

export type ZDateStringFromInputParams = {
  /** When the value is missing or blank after trim */
  requiredMessage?: string;
  /** When present but not a valid `YYYY-MM-DD` calendar date */
  message?: string;
  /** @deprecated Use {@link message} */
  invalidMessage?: string;
};

/**
 * Required `YYYY-MM-DD` string (typical for {@link ControlledDateField} / `input[type=date]`).
 */
export function zDateStringFromInput(params?: ZDateStringFromInputParams) {
  const invalidMsg =
    params?.message?.trim() ||
    params?.invalidMessage?.trim() ||
    DEFAULT_DATE_INVALID;
  const requiredMsg =
    params?.requiredMessage?.trim() ||
    DEFAULT_DATE_REQUIRED;

  return z.preprocess(
    (val) => (typeof val === 'string' ? val.trim() : val),
    z
      .string()
      .min(1, requiredMsg)
      .refine(isValidIsoDateString, { message: invalidMsg })
  );
}

export type ZOptionalDateStringFromInputParams = {
  message?: string;
  /** @deprecated Use {@link message} */
  invalidMessage?: string;
};

/**
 * Optional `YYYY-MM-DD` string; empty input becomes `undefined`.
 */
export function zOptionalDateStringFromInput(
  params?: ZOptionalDateStringFromInputParams
) {
  const invalidMsg =
    params?.message?.trim() ||
    params?.invalidMessage?.trim() ||
    DEFAULT_DATE_INVALID;

  return z.preprocess(
    preprocessOptionalDateStringInput,
    z.union([
      z.undefined(),
      z.string().refine(isValidIsoDateString, { message: invalidMsg }),
    ])
  );
}
